import { useNavigate, useParams } from 'react-router-dom';
import { BpSubpage } from '../../../components/BpSubpage/BpSubpage';
import { FC } from 'react';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { showErrorToast } from '../../../utils/showErrorToast';
import { showSuccessToast } from '../../../utils/showSuccessToast';
import { t } from 'i18next';
import { graphql } from 'client/gql';
import { MaterialDetail } from '../../../components/MaterialDetail/MaterialDetail';
import { GroupWorkMaterialsTable } from '../../../components/WorkMaterialsTable/GroupWorkMaterialsTable';
import { GroupWorkMaterialsForm } from '../../../components/WorkMaterialsForm/GroupWorkMaterialsForm';
import { ErrorState } from '@bp/ui-components';
import { NotificationReason } from '../../../utils/matrixClient';
import { useMutation, useQuery } from 'urql';
import { usePermissionChecker } from 'hooks/usePermissionChecker';
import { backendApi } from '../../../utils/backendApi';

const DeleteWorkMaterialsMutationDocument = graphql(`
  mutation BpDeleteWorkMaterialsFromGroupWorkMaterials($where: WorkMaterialWhere!) {
    deleteWorkMaterials(where: $where) {
      nodesDeleted
      relationshipsDeleted
    }
  }
`);

const GroupWorkMaterialsSubpagePageQueryDocument = graphql(`
  query GroupWorkMaterialsSubpagePageData($profile: ID!, $groupUuid: ID!, $workMaterialUuid: ID!) {
    ...PermissionCheckerFields
    groups(where: { uuid: $groupUuid }) {
      uuid
      __typename
      name
      teachingUnits {
        uuid
        __typename
      }
    }
    ...MaterialDetailFields
    ...GroupWorkMaterialsFormFields
    ...GroupWorkMaterialsTableFields
  }
`);

export const GroupWorkMaterialsSubpage: FC<{ variant: 'add' | 'edit' | 'detail' | 'table' }> = ({ variant }) => {
  const { uuid, groupUuid } = useParams<{ add: string; edit: string; uuid: string; groupUuid: string }>();
  const navigate = useNavigate();
  const { pimAuthClaims } = useAuthClaims();
  const profileUuid = pimAuthClaims.getProfile().uuid;

  const [{ data }] = useQuery({
    query: GroupWorkMaterialsSubpagePageQueryDocument,
    variables: {
      profile: profileUuid,
      groupUuid: groupUuid!,
      workMaterialUuid: uuid ?? '',
    },
    pause: groupUuid === undefined,
  });
  const permissions = usePermissionChecker(data);

  const teachingUnitUuid = data?.groups[0]?.teachingUnits[0]?.uuid;

  const hasPermission = groupUuid
    ? permissions?.canCreateWorkmaterial({
        uuid: groupUuid,
        organization: { uuid: pimAuthClaims.getOrganization().uuid },
      })
    : false;

  const [, deleteWorkMaterial] = useMutation(DeleteWorkMaterialsMutationDocument);

  const handleDelete = async (uuid: string, title: string) => {
    const res = await deleteWorkMaterial({
      where: {
        uuid,
      },
    });
    if (res.error) {
      showErrorToast(res.error);
    } else {
      showSuccessToast(t('common.success'));
      void backendApi.notify({
        type: NotificationReason.GroupMaterialDeleted,
        groupUuid: groupUuid ?? '',
        subjectName: title,
        groupName: data?.groups[0]?.name ?? '',
      });
    }
  };

  return !hasPermission && (variant === 'edit' || variant === 'add') ? (
    <ErrorState type={'forbidden'} onNavigateBack={() => navigate(`/groups/${groupUuid}/workmaterials/`)} />
  ) : (!teachingUnitUuid || !groupUuid) && (variant === 'edit' || variant === 'add') ? (
    <ErrorState onNavigateBack={() => navigate(`/groups/${groupUuid}/workmaterials/`)} />
  ) : (
    <BpSubpage>
      {(variant === 'edit' || variant === 'add') && teachingUnitUuid && groupUuid && hasPermission && (
        <GroupWorkMaterialsForm
          teachingUnitUuid={teachingUnitUuid}
          canArchive={variant === 'edit'}
          onClose={() => navigate(`/groups/${groupUuid}/workmaterials/`)}
          workMaterialUuid={uuid ? uuid : undefined}
          query={data}
        />
      )}
      {variant === 'detail' && uuid && (
        <MaterialDetail onClose={() => navigate(`/groups/${groupUuid}/workmaterials/`)} query={data} />
      )}
      {variant === 'table' && groupUuid && (
        <GroupWorkMaterialsTable
          groupUuid={groupUuid}
          onAdd={() => {
            navigate(`/groups/${groupUuid}/workmaterials/add`);
          }}
          onEdit={(uuid) => {
            navigate(`/groups/${groupUuid}/workmaterials/${uuid}/edit`);
          }}
          onDelete={async (uuid, title) => {
            await handleDelete(uuid, title);
          }}
          onShow={(uuid) => {
            navigate(`/groups/${groupUuid}/workmaterials/${uuid}`);
          }}
          query={data}
        />
      )}
    </BpSubpage>
  );
};
